/**
 * closedOpportunityMixin
 *
 * PURPOSE
 * Handles Salesforce closed-won / closed-lost recovery flow for calculator applications.
 *
 * DEPENDS ON HOST COMPONENT PROVIDING:
 * - this.vehicleApplication
 * - this.user
 * - this.shortlist
 * - this.updateShortList()
 * - this.updateShortListDB(applicationId, userId)
 * - this.updateVehicleApplication(value, domain, type, field)
 * - this.isUserValidForCreditApp (boolean computed)
 * - Apollo queries:
 *    - this.getSalesforceAccountByUser
 *    - this.getBusinessInfo
 *
 * SIDE EFFECTS
 * - Clones Salesforce opportunity
 * - Unarchives application
 * - Clears Vuex user + vehicleApplication
 * - Hard reloads to dashboard
 */

import { mapMutations, mapActions } from 'vuex';
import MutateCreateClonedOpportunity from '@/graphql/mutations/MutateCreateClonedOpportunity';
import MutateUnarchiveVehicleApplication from '@/graphql/mutations/MutateUnarchiveVehicleApp';
import { isClosedOpportunityStage, waitUntil } from '@/utils/helpers';

export default {
  data() {
    return {
      closedOpp_isClosedOpportunity: false,
      closedOpp_archivedTimer: null
    };
  },

  methods: {
    ...mapActions({
      setApploading: 'app/setAppLoading'
    }),
    ...mapMutations({
      closedOpp_clearUser: 'auth/clearUser',
      closedOpp_clearVehicleApplication: 'vehicleApplication/clearVehicleApplication'
    }),

    /**
     * PUBLIC ENTRY POINT
     * Call this from Apollo opportunity result()
     */
    async closedOpp_processOpportunity(opportunity) {
      if (!opportunity) return;

      if (isClosedOpportunityStage(opportunity?.StageName)) {
        await this.closedOpp_handleClosedOpportunity(opportunity);
      }

      this.closedOpp_tryUnarchiveAndReload();
    },

    /**
     * Marks app as closed + clones opportunity
     */
    async closedOpp_handleClosedOpportunity(opportunity) {
      this.closedOpp_isClosedOpportunity = true;
      // Business decision: first-time closed wipes shortlist

      this.setApploading(true);
      this.shortlist = [];
      this.updateShortList();

      if (this.vehicleApplication?.applicationId && this.user?.id) {
        await this.updateShortListDB(this.vehicleApplication.applicationId, this.user.id);
      }

      const response = await this.$apollo.mutate({
        mutation: MutateCreateClonedOpportunity,
        fetchPolicy: 'no-cache',
        variables: {
          opportunityId: opportunity.Id
        }
      });

      const newOpportunityId = response?.data?.createClonedOpportunity?.opportunityId;

      if (newOpportunityId) {
        await this.updateVehicleApplication(newOpportunityId, 'salesforce', 'opportunity', 'opportunityId');
      }
      this.setApploading(false);
    },

    /**
     * If business rules allow, unarchive and reboot app cleanly
     */
    async closedOpp_tryUnarchiveAndReload() {
      if (!this.closedOpp_isClosedOpportunity) return;

      this.setApploading(true);

      const appId = this.vehicleApplication?.applicationId;
      if (!appId) return;

      const reloadKey = `closedOpp_reload_${appId}`;
      if (sessionStorage.getItem(reloadKey)) return;

      const ready = await waitUntil(
        () =>
          !this.$apollo?.queries?.getSalesforceAccountByUser?.loading &&
          !this.$apollo?.queries?.getBusinessInfo?.loading &&
          !!this.getSalesforceAccountByUser &&
          !!this.getBusinessInfo,
        { retries: 30, delay: 150 }
      );

      if (!ready) return;
      if (!this.closedOpp_isEligibleForReactivation()) return;

      sessionStorage.setItem(reloadKey, '1');

      await this.$apollo.mutate({
        mutation: MutateUnarchiveVehicleApplication,
        fetchPolicy: 'no-cache',
        variables: {
          applicationId: appId,
          userEmail: this.user?.email
        }
      });

      // Clear persisted frontend state
      this.closedOpp_clearVehicleApplication();
      this.closedOpp_clearUser();

      this.setApploading(true);
      // Hard reboot to dashboard
      window.location.href = this.$router.resolve({ name: 'dashboard' }).href;
    },

    closedOpp_isEligibleForReactivation() {
      const NZ_ENTITY_TYPE = ['ULTD', 'LTD', 'Sole_Trader'];
      const AU_ENTITY_TYPE = ['IND', 'PRV', 'PUB'];

      const entityType = this.getBusinessInfo?.rows?.[0]?.entityType;
      const account = this.getSalesforceAccountByUser;

      const isEntityTypeValid = this.region === 'NZ' ? NZ_ENTITY_TYPE.includes(entityType) : AU_ENTITY_TYPE.includes(entityType);

      return isEntityTypeValid && account?.AccountSource === 'Calculator' && account?.Credit_Total_Value__c == null;
    }
  }
};
